import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  alpha,
  Button,
  Icon,
  useMediaQuery,
  Paper,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Lottie from "react-lottie";
import animationData from "../../../../assets/illustrations/server-upload.json";

interface SolutionBlock {
  id: string;
  icon: string;
  title: string;
  problem: string;
  description: string;
  highlights: string[];
  gradient: string;
}

const solutionBlocks: SolutionBlock[] = [
  {
    id: "inventory-loss",
    icon: "inventory_2",
    title: "Smart Inventory Control",
    problem: "Losing Money on Expired Stock?",
    description:
      "Our intelligent tracking system monitors every ingredient's lifecycle, alerting you before items expire. Save thousands in potential losses.",
    highlights: ["Expiry Prevention", "Real-time Alerts", "Cost Savings"],
    gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
  },
  {
    id: "hidden-costs",
    icon: "trending_up",
    title: "Profit Guardian",
    problem: "Margins Shrinking Without Warning?",
    description:
      "Watch your costs in real-time. Our system automatically adjusts prices when ingredient costs change, protecting your margins 24/7.",
    highlights: [
      "Auto Cost Updates",
      "Margin Protection",
      "Price Optimization",
    ],
    gradient: "linear-gradient(135deg, #0891B2 0%, #0E7490 100%)",
  },
  {
    id: "recipe-standardization",
    icon: "restaurant_menu",
    title: "Recipe Master",
    problem: "Inconsistent Food Quality?",
    description:
      "One recipe, one standard, every time. Ensure perfect consistency across all locations with our digital recipe management system.",
    highlights: ["Perfect Consistency", "Digital Standards", "Quality Control"],
    gradient: "linear-gradient(135deg, #0E7490 0%, #155E75 100%)",
  },
  {
    id: "menu-chaos",
    icon: "menu_book",
    title: "Menu Intelligence",
    problem: "Menu & POS Not in Sync?",
    description:
      "Seamlessly connect your menu to inventory and POS. Prices update automatically, items disable when out of stock, and profitability is always clear.",
    highlights: ["Auto-Sync", "Smart Pricing", "Stock Control"],
    gradient: "linear-gradient(135deg, #155E75 0%, #164E63 100%)",
  },
  {
    id: "branch-visibility",
    icon: "account_tree",
    title: "Multi-Location Command Center",
    problem: "Lost Control of Multiple Locations?",
    description:
      "One dashboard to rule them all. Monitor every location's performance, stock levels, and staff activity from a single, powerful interface.",
    highlights: ["Central Control", "Real-time View", "Performance Tracking"],
    gradient: "linear-gradient(135deg, #164E63 0%, #083344 100%)",
  },
  {
    id: "access-control",
    icon: "admin_panel_settings",
    title: "Smart Access Control",
    problem: "Security Risks Keeping You Up?",
    description:
      "Sleep better knowing your system is secure. Set precise permissions for every role, ensuring staff can access only what they need.",
    highlights: ["Role-based Access", "Secure Control", "Peace of Mind"],
    gradient: "linear-gradient(135deg, #083344 0%, #082F49 100%)",
  },
  {
    id: "pos-experience",
    icon: "point_of_sale",
    title: "Lightning-Fast POS",
    problem: "Slow Service Frustrating Customers?",
    description:
      "Speed up service with our intuitive POS. Designed for quick orders, easy modifications, and seamless payment processing.",
    highlights: ["Rapid Service", "Easy Training", "Happy Customers"],
    gradient: "linear-gradient(135deg, #082F49 0%, #172554 100%)",
  },
  {
    id: "audit-trail",
    icon: "fact_check",
    title: "Complete Transparency",
    problem: "Mysterious Changes Causing Chaos?",
    description:
      "Track every action with crystal-clear audit trails. Know exactly who did what and when, maintaining full accountability.",
    highlights: ["Full Tracking", "Clear History", "Total Control"],
    gradient: "linear-gradient(135deg, #172554 0%, #1E1B4B 100%)",
  },
];

const SolutionCard: React.FC<SolutionBlock & { index: number }> = ({
  icon,
  title,
  problem,
  description,
  highlights,
  gradient,
  index,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{ height: "100%" }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          height: "100%",
          background: "rgba(255, 255, 255, 0.98)",
          borderRadius: "20px",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.05)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.08)",
            "& .solution-icon": {
              transform: "scale(1.1) rotate(5deg)",
            },
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: gradient,
          },
        }}
      >
        <Box
          className="solution-icon"
          sx={{
            width: "56px",
            height: "56px",
            borderRadius: "16px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
          }}
        >
          <span
            className="material-icons"
            style={{ color: "white", fontSize: "28px" }}
          >
            {icon}
          </span>
        </Box>
        <Typography
          sx={{
            color: "error.main",
            fontSize: "0.9rem",
            fontWeight: 600,
            mb: 1,
            opacity: 0.9,
            fontFamily: "Montserrat",
            letterSpacing: "0.01em",
          }}
        >
          {problem}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 700,
            fontSize: "1.25rem",
            mb: 2,
            color: "#1A2027",
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "#4A5568",
            fontSize: "0.95rem",
            lineHeight: 1.7,
            fontFamily: "Montserrat",
            mb: 3,
            flex: 1,
          }}
        >
          {description}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            mt: "auto",
          }}
        >
          {highlights.map((highlight, i) => (
            <Box
              key={i}
              sx={{
                py: 0.75,
                px: 2,
                borderRadius: "30px",
                background: (theme) =>
                  `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.08
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                border: "1px solid",
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon
                sx={{
                  fontSize: 16,
                  color: "primary.main",
                }}
              >
                check_circle
              </Icon>
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "primary.main",
                  fontFamily: "Montserrat",
                  letterSpacing: "0.02em",
                }}
              >
                {highlight}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </motion.div>
  );
};

// Background Pattern Components
const GridPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: "64px 64px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const DotsPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 2px, transparent 2px)`,
      backgroundSize: "32px 32px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const CircleGlow = () => (
  <Box
    sx={{
      position: "absolute",
      width: "60%",
      paddingBottom: "60%",
      background:
        "radial-gradient(circle at center, rgba(6, 182, 212, 0.15) 0%, transparent 70%)",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 0,
      animation: "pulse 8s ease-in-out infinite",
      "@keyframes pulse": {
        "0%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
        "50%": {
          transform: "translate(-50%, -50%) scale(1.2)",
          opacity: 0.3,
        },
        "100%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
      },
    }}
  />
);

export const Solutions: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 10, md: 14 },
          background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <GridPattern />
        <DotsPattern />
        <CircleGlow />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.4,
            background:
              "radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.15) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 40%)",
            zIndex: 0,
            animation: "float 15s ease-in-out infinite",
            "@keyframes float": {
              "0%": {
                transform: "translateY(0) scale(1)",
              },
              "50%": {
                transform: "translateY(-20px) scale(1.05)",
              },
              "100%": {
                transform: "translateY(0) scale(1)",
              },
            },
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.75rem", md: "3.75rem" },
                    fontWeight: 800,
                    mb: 3,
                    color: "white",
                    fontFamily: "Montserrat",
                    lineHeight: 1.2,
                    letterSpacing: "-0.02em",
                    textShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: -16,
                      left: 0,
                      width: "120px",
                      height: "4px",
                      background: "linear-gradient(90deg, #06B6D4, #22D3EE)",
                      borderRadius: "2px",
                    },
                  }}
                >
                  Your Problems
                  <br />
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(90deg, #06B6D4, #0891B2)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Our Solutions
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    color: "rgba(255,255,255,0.95)",
                    fontWeight: 500,
                    lineHeight: 1.6,
                    fontFamily: "Montserrat",
                    fontSize: { xs: "1.25rem", md: "1.35rem" },
                    maxWidth: "600px",
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  We understand the daily challenges of running a restaurant.
                  That's why we've built solutions that actually work—designed
                  with operators, for operators.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/contact")}
                  endIcon={<Icon>arrow_forward</Icon>}
                  sx={{
                    background: "linear-gradient(90deg, #06B6D4, #0891B2)",
                    color: "white",
                    px: 5,
                    py: 2,
                    fontSize: "1.15rem",
                    borderRadius: "14px",
                    textTransform: "none",
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                    boxShadow: "0 12px 32px rgba(6, 182, 212, 0.25)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #0891B2, #06B6D4)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 16px 40px rgba(6, 182, 212, 0.35)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Let's Solve This Together
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    filter: "drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1))",
                  }}
                >
                  <Lottie
                    options={{
                      animationData: animationData,
                      loop: true,
                      autoplay: true,
                      rendererSettings: {
                        preserveAspectRatio: "xMidYMid slice",
                      },
                    }}
                    width={isMobile ? 320 : 540}
                    height={isMobile ? 320 : 540}
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Solutions Grid */}
      <Box
        sx={{
          py: { xs: 12, md: 16 },
          background: "white",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.03) 0%, transparent 70%)",
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: "#1A2027",
                fontFamily: "Montserrat",
                fontSize: { xs: "2.25rem", md: "3rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Every Problem Has a Solution
            </Typography>
            <Typography
              variant="h6"
              align="center"
              sx={{
                mb: 8,
                color: "#4A5568",
                fontFamily: "Montserrat",
                fontWeight: 400,
                maxWidth: "800px",
                mx: "auto",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.6,
              }}
            >
              Click through our solutions below and see how we can help
              transform your restaurant operations from chaos to control.
            </Typography>
          </motion.div>
          <Grid container spacing={4}>
            {solutionBlocks.map((solution, index) => (
              <Grid item xs={12} md={6} lg={4} key={solution.id}>
                <SolutionCard {...solution} index={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Closing Section */}
      <Box
        sx={{
          py: { xs: 12, md: 16 },
          background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.05) 0%, transparent 70%)",
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontWeight: 800,
                mb: 3,
                color: "#1A2027",
                fontFamily: "Montserrat",
                fontSize: { xs: "2.25rem", md: "3rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.3,
              }}
            >
              Ready to Transform
              <br />
              Your Restaurant Operations?
            </Typography>
            <Typography
              variant="h5"
              align="center"
              sx={{
                mb: 6,
                maxWidth: 800,
                mx: "auto",
                color: "#4A5568",
                fontFamily: "Montserrat",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Join hundreds of successful restaurants who've already made the
              switch to smarter operations with VenFlows.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/auth/register")}
                sx={{
                  background: "linear-gradient(90deg, #06B6D4, #0891B2)",
                  color: "white",
                  px: 5,
                  py: 2,
                  fontSize: "1.15rem",
                  borderRadius: "14px",
                  textTransform: "none",
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  boxShadow: "0 12px 32px rgba(6, 182, 212, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #0891B2, #06B6D4)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 16px 40px rgba(6, 182, 212, 0.35)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Start Your Transformation
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/contact")}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: "1.15rem",
                  borderColor: "#06B6D4",
                  borderWidth: "2px",
                  color: "#06B6D4",
                  borderRadius: "14px",
                  textTransform: "none",
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#0891B2",
                    borderWidth: "2px",
                    background: "rgba(6, 182, 212, 0.05)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Book a Demo
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};
